import React from "react"
import { t } from "ttag"
import { IconButton } from "@humanpredictiveintelligence/myqvt-library"

import config from "config/business"
import { NotificationContext } from "features/Notification"
import { DeleteQuestionDialog, QuestionClassicDialog, QuestionNpsDialog, QuestionsList } from "../../components"
import { evictSurveysWithQuestionFromCache, evictThemesWithCustomQuestionsQueryFromCache } from "../../utils"
import {
  CustomQuestion, CustomQuestionPayload,
  CustomQuestionWithChoicesPayload,
  CustomQuestionsPagingInfo,
} from "models/CustomQuestions"
import { QuestionType } from "models/generated"

import { useCreateCustomQuestionMutation } from "graphql/mutations/generated/CreateCustomQuestion"
import { useDeleteCustomQuestionMutation } from "graphql/mutations/generated/DeleteCustomQuestion"
import { useUpdateCustomQuestionMutation } from "graphql/mutations/generated/UpdateCustomQuestion"
import { useGetCustomQuestionsQuery } from "graphql/queries/generated/GetCustomQuestions"

import * as Styles from "./QuestionsPage.styles"

export const QuestionsPage = () => {
  const notification = React.useContext(NotificationContext)

  const [ isCreateDialogOpen, setIsCreateDialogOpen ] = React.useState(false)
  const [ isCreateNpsDialogOpen, setIsCreateNpsDialogOpen ] = React.useState(false)
  const [ questionToEdit, setQuestionToEdit ] = React.useState<CustomQuestion | null>(null)
  const [ questionToDelete, setQuestionToDelete ] = React.useState<CustomQuestion | null>(null)

  const [ pagingInfo, setPagingInfo ] = React.useState<CustomQuestionsPagingInfo>({
    currentPage: 1,
    pageSize: config.survey.customQuestions.pageSizes[0],
  })

  const { data, loading: areQuestionsLoading, refetch: refetchQuestions } = useGetCustomQuestionsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      limit: pagingInfo.pageSize,
      skip: (pagingInfo.currentPage - 1) * pagingInfo.pageSize,
    },
  })

  const [ createCustomQuestion, { loading: isCreatingQuestion } ] = useCreateCustomQuestionMutation({
    update: evictThemesWithCustomQuestionsQueryFromCache,
  })
  const [ updateCustomQuestion, { loading: isUpdatingQuestion } ] = useUpdateCustomQuestionMutation({
    update: (cache, mutation) => {
      evictThemesWithCustomQuestionsQueryFromCache(cache)

      if (mutation.data?.updateCustomQuestion.id) {
        evictSurveysWithQuestionFromCache(cache, mutation.data.updateCustomQuestion.id)
      }
    },
  })
  const [ deleteCustomQuestion, { loading: isDeletingQuestion } ] = useDeleteCustomQuestionMutation({
    update: evictThemesWithCustomQuestionsQueryFromCache,
  })

  return (
    <Styles.Container>
      <Styles.Header>
        <Styles.QuestionTitle>{t`My questions`}</Styles.QuestionTitle>

        <Styles.ButtonContainer>
          <IconButton
            onClick={() => setIsCreateDialogOpen(true)}
            icon="edit"
            size="big"
          >
            {t`Create a question`}
          </IconButton>

          <IconButton
            onClick={() => setIsCreateNpsDialogOpen(true)}
            icon="edit"
            size="big"
            link=""
          >
            {t`Create an NPS question`}
          </IconButton>
        </Styles.ButtonContainer>

      </Styles.Header>

      {isCreateDialogOpen && (
        <QuestionClassicDialog
          isLoading={isCreatingQuestion}
          onClose={() => setIsCreateDialogOpen(false)}
          onValidate={createQuestion}
        />
      )}

      {isCreateNpsDialogOpen && (
        <QuestionNpsDialog
          isLoading={isCreatingQuestion}
          onClose={() => setIsCreateNpsDialogOpen(false)}
          onValidate={createNpsQuestion}
        />
      )}

      {questionToEdit && (
        <QuestionClassicDialog
          customQuestion={questionToEdit}
          isLoading={isUpdatingQuestion}
          onClose={() => setQuestionToEdit(null)}
          onValidate={updateQuestion}
        />
      )}

      {questionToDelete && (
        <DeleteQuestionDialog
          isLoading={isDeletingQuestion}
          onClose={() => setQuestionToDelete(null)}
          onConfirm={deleteQuestion}
        />
      )}

      <QuestionsList
        questions={data?.getCustomQuestions.questions ?? []}
        count={data?.getCustomQuestions.count ?? 0}
        loading={areQuestionsLoading}
        pagingInfo={pagingInfo}
        onPagingInfoChange={setPagingInfo}
        onEditQuestion={setQuestionToEdit}
        onDeleteQuestion={setQuestionToDelete}
      />
    </Styles.Container>
  )

  async function createQuestion(payload: CustomQuestionWithChoicesPayload): Promise<void> {
    try {
      await createCustomQuestion({
        variables: {
          choices: payload.choices,
          name: payload.name,
        },
      })

      setIsCreateDialogOpen(false)
      setIsCreateNpsDialogOpen(false)

      notification.show(
        t`Success`,
        t`The question has been added`,
        "success",
      )

      await refetchQuestions()
    }
    catch (error) {
      notification.show(
        t`Error`,
        t`The question could not be added`,
        "danger",
      )

      throw error
    }
  }

  async function createNpsQuestion(payload: CustomQuestionPayload): Promise<void> {
    try {
      await createCustomQuestion({
        variables: {
          name: payload.name,
          type: QuestionType.Nps,
        },
      })

      setIsCreateNpsDialogOpen(false)
      notification.show(
        t`Success`,
        t`The NPS question has been added`,
        "success",
      )

      await refetchQuestions()
    }
    catch (error) {
      notification.show(
        t`Error`,
        t`The NPS question could not be added`,
        "danger",
      )

      throw error
    }
  }

  async function updateQuestion(payload: CustomQuestionWithChoicesPayload): Promise<void> {
    if (!questionToEdit) {
      return
    }

    try {
      await updateCustomQuestion({
        variables: {
          choices: payload.choices,
          name: payload.name,
          questionId: questionToEdit.id,
        },
      })

      setQuestionToEdit(null)
      notification.show(
        t`Success`,
        t`The question has been updated`,
        "success",
      )

      await refetchQuestions()
    }
    catch (error) {
      notification.show(
        t`Error`,
        t`The question could not be updated`,
        "danger",
      )

      throw error
    }
  }

  async function deleteQuestion(): Promise<void> {
    if (!questionToDelete) {
      return
    }

    try {
      await deleteCustomQuestion({
        variables: {
          questionId: questionToDelete.id,
        },
      })

      setQuestionToDelete(null)
      notification.show(
        t`Success`,
        t`The question has been deleted`,
        "success",
      )

      await refetchQuestions()
    }
    catch (error) {
      notification.show(
        t`Error`,
        t`The question could not be deleted`,
        "danger",
      )

      throw error
    }
  }
}
